import React, { useState } from 'react';
import { Upload, Lock, Loader2, BookOpen } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from '../hooks/use-toast';
import api, { getToken, setToken } from '../lib/api';

export default function DosageUploadDialog({ open, onOpenChange, onUploaded }) {
  const [authed, setAuthed] = useState(!!getToken());
  const [passcode, setPasscode] = useState('');
  const [title, setTitle] = useState('');
  const [verseRef, setVerseRef] = useState('');
  const [verse, setVerse] = useState('');
  const [body, setBody] = useState('');
  const [minutes, setMinutes] = useState('3');
  const [postDate, setPostDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [image, setImage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const reset = () => {
    setTitle(''); setVerseRef(''); setVerse(''); setBody(''); setMinutes('3');
    setPostDate(new Date().toISOString().slice(0, 10));
    setImage(null); setPasscode(''); setProgress(0);
  };

  const signIn = async (e) => {
    e.preventDefault();
    if (!passcode.trim()) return;
    try {
      const { token } = await api.login(passcode.trim());
      setToken(token); setAuthed(true);
      toast({ title: 'Signed in', description: 'You may now publish a dosage.' });
    } catch {
      toast({ title: 'Incorrect passcode' });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title || !body) { toast({ title: 'Title and devotional body are required' }); return; }
    setBusy(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append('title', title);
      fd.append('verse_ref', verseRef || '');
      fd.append('verse', verse || '');
      fd.append('body', body);
      fd.append('minutes', String(parseInt(minutes || '3', 10) || 3));
      if (postDate) fd.append('post_date', postDate);
      if (image) fd.append('image', image);
      await api.createDosage(fd, (evt) => {
        if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
      });
      toast({ title: 'Dosage published', description: 'The new devotional is now live.' });
      reset();
      onUploaded && onUploaded();
      onOpenChange(false);
    } catch (err) {
      toast({ title: 'Publish failed', description: String(err?.response?.data?.detail || err.message) });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-lg bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-teal-deep" /> Publish a Daily Dosage
          </DialogTitle>
        </DialogHeader>

        {!authed ? (
          <form onSubmit={signIn} className="space-y-4 mt-2">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-cream/60">
              <Lock className="h-4 w-4 text-teal-deep" />
              <p className="text-sm text-ink/70">Publishing is protected. Enter the ministry passcode to continue.</p>
            </div>
            <div>
              <Label htmlFor="dpc">Passcode</Label>
              <Input id="dpc" type="password" value={passcode} onChange={e => setPasscode(e.target.value)} placeholder="Enter passcode" autoFocus />
            </div>
            <Button type="submit" className="w-full bg-teal-brand hover:bg-teal-deep text-white h-11">Continue</Button>
          </form>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div>
              <Label htmlFor="dt">Title *</Label>
              <Input id="dt" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. The Prescription of Grace" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="dvr">Verse Reference</Label>
                <Input id="dvr" value={verseRef} onChange={e => setVerseRef(e.target.value)} placeholder="John 3:16" />
              </div>
              <div>
                <Label htmlFor="dmin">Read time (min)</Label>
                <Input id="dmin" type="number" min="1" max="30" value={minutes} onChange={e => setMinutes(e.target.value)} />
              </div>
            </div>
            <div>
              <Label htmlFor="dpd">Post date</Label>
              <Input id="dpd" data-testid="dosage-post-date" type="date" value={postDate} onChange={e => setPostDate(e.target.value)} />
              <p className="text-xs text-ink/50 mt-1">Used to arrange posts chronologically. Defaults to today.</p>
            </div>
            <div>
              <Label htmlFor="dv">Verse text</Label>
              <Textarea id="dv" rows={2} value={verse} onChange={e => setVerse(e.target.value)} placeholder='"For God so loved the world…"' />
            </div>
            <div>
              <Label htmlFor="db">Devotional body *</Label>
              <Textarea id="db" rows={6} value={body} onChange={e => setBody(e.target.value)} placeholder="Write your devotional here…" />
            </div>
            <div>
              <Label htmlFor="di">Cover image (optional)</Label>
              <Input id="di" type="file" accept="image/*" onChange={e => setImage(e.target.files?.[0] || null)} />
              {image && <p className="text-xs text-ink/50 mt-1">{image.name}</p>}
            </div>
            {busy && (
              <div className="p-3 rounded-lg bg-cream/60 border border-teal-brand/20">
                <div className="flex items-center gap-2 text-sm text-ink">
                  <Loader2 className="h-4 w-4 animate-spin text-teal-deep" /> Publishing… {progress}%
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-white overflow-hidden">
                  <div className="h-full bg-gold-brand transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Button type="submit" disabled={busy} className="flex-1 bg-gold-brand hover:bg-[#C48A1F] text-ink font-semibold h-11">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Publish Dosage
              </Button>
              <Button type="button" variant="outline" onClick={() => { setToken(null); setAuthed(false); }} className="h-11" title="Sign out">
                <Lock className="h-4 w-4" />
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
